import React, { useEffect, useMemo, useState } from "react";
import { logWarn } from "../lib/logger";
import { api, STATUSES, statusLabel, statusColor } from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { BarChart3, Clock, TrendingUp } from "lucide-react";

const WINDOWS = [
  { v: "30", label: "Last 30 days" },
  { v: "60", label: "Last 60 days" },
  { v: "90", label: "Last 90 days" },
  { v: "180", label: "Last 180 days" },
  { v: "365", label: "Last 12 months" },
];

const fmtDays = (d) => (d >= 1 ? `${d} day${d === 1 ? "" : "s"}` : `${Math.round(d * 24)} hrs`);

// "rejected" is intentionally excluded from the time-in-stage view.
const STAGE_KEYS = STATUSES.filter((s) => s.key !== "rejected");

const DATE_FMT = { month: "short", day: "numeric" };
const CHART_MARGIN = { top: 4, right: 8, left: -20, bottom: 4 };
const TOOLTIP_CURSOR = { fill: "#f4f4f5" };
const TOOLTIP_CONTENT_STYLE = { border: "2px solid #18181b", borderRadius: 0, fontSize: 12 };
const TOOLTIP_LABEL_STYLE = { fontWeight: 700 };
const AXIS_TICK = { fontSize: 10 };
const CHART_BOX = { width: "100%", height: 280 };

export default function Metrics() {
  const [windowDays, setWindowDays] = useState("90");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/metrics?window_days=${windowDays}`)
      .then((r) => setData(r.data))
      .catch((e) => logWarn("metrics load failed", e))
      .finally(() => setLoading(false));
  }, [windowDays]);

  const maxAvg = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...STAGE_KEYS.map((s) => data.avg_days_in_stage[s.key] || 0));
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.applied_series.map((p) => ({
      ...p,
      label: new Date(p.date + "T00:00:00").toLocaleDateString(undefined, DATE_FMT),
    }));
  }, [data]);

  return (
    <div className="space-y-8" data-testid="metrics-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tiny flex items-center gap-2"><BarChart3 size={13} /> Insights</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Metrics</h1>
          <p className="text-zinc-600 mt-2">How your pipeline is moving — drawn from your status history.</p>
        </div>
        <Select value={windowDays} onValueChange={setWindowDays}>
          <SelectTrigger className="rounded-none h-10 w-44 border-zinc-300" data-testid="metrics-window-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {WINDOWS.map((w) => (
              <SelectItem key={w.v} value={w.v} data-testid={`window-${w.v}`}>{w.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{["a", "b", "c"].map((k) => <div key={k} className="h-28 bg-zinc-100" />)}</div>
      ) : !data ? (
        <div className="brutal-card p-10 text-center"><div className="label-tiny">Could not load metrics</div></div>
      ) : (
        <>
          {/* summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="metrics-summary">
            <Stat label="Total jobs" value={data.total_jobs} />
            <Stat label="Applied (all time)" value={data.total_applied_all_time} />
            <Stat label={`Applied (window)`} value={data.applied_in_window} />
            <Stat label="Window" value={`${data.window_days}d`} />
          </div>

          {/* avg days in stage */}
          <section className="brutal-card p-6" data-testid="avg-stage-section">
            <div className="label-tiny flex items-center gap-2 mb-5"><Clock size={13} /> Average time spent in each stage</div>
            <div className="space-y-4">
              {STAGE_KEYS.map((s) => {
                const d = data.avg_days_in_stage[s.key] || 0;
                return (
                  <div key={s.key} data-testid={`avg-stage-${s.key}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="flex items-center gap-2">
                        <span className="status-dot" style={{ backgroundColor: s.color }} />
                        {statusLabel(s.key)}
                      </span>
                      <span className="font-mono-jb text-zinc-600">{d ? fmtDays(d) : "—"}</span>
                    </div>
                    <div className="h-2.5 bg-zinc-100">
                      <div className="h-full transition-all" style={{ width: `${(d / maxAvg) * 100}%`, backgroundColor: s.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* applied over time */}
          <section className="brutal-card p-6" data-testid="applied-chart-section">
            <div className="label-tiny flex items-center gap-2 mb-5"><TrendingUp size={13} /> Applications over time</div>
            {data.applied_in_window === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center" data-testid="applied-empty">No applications in this window yet.</p>
            ) : (
              <div style={CHART_BOX}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                    <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" minTickGap={28} stroke="#a1a1aa" />
                    <YAxis allowDecimals={false} tick={AXIS_TICK} stroke="#a1a1aa" />
                    <Tooltip
                      cursor={TOOLTIP_CURSOR}
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                    />
                    <Bar dataKey="count" fill="#18181b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="brutal-card p-4">
    <div className="font-heading text-2xl sm:text-3xl font-black tracking-tight">{value}</div>
    <div className="label-tiny mt-1">{label}</div>
  </div>
);
